export type Locale = 'zh-CN' | 'zh-TW' | 'en';

// Accept any string here — Prisma's generated Region enum and our shared Region enum are
// structurally identical but nominally distinct in TS, so we use the loose string form.
// Unknown values are dropped silently by the helpers below.
type RegionLike = string | null | undefined;
type RegionArrayLike = readonly string[] | null | undefined;

const REGION_LABELS: Record<string, Record<Locale, string>> = {
  SOUTH_BAY: { 'zh-CN': '南湾', 'zh-TW': '南灣', en: 'South Bay' },
  PENINSULA: { 'zh-CN': '半岛', 'zh-TW': '半島', en: 'Peninsula' },
  EAST_BAY: { 'zh-CN': '东湾', 'zh-TW': '東灣', en: 'East Bay' },
  SAN_FRANCISCO: { 'zh-CN': '旧金山', 'zh-TW': '舊金山', en: 'San Francisco' },
  OTHER: { 'zh-CN': '其他', 'zh-TW': '其他', en: 'Other' },
};

// Display order used to stabilize multi-region truncation (PRD §4.3).
// Matches the enum declaration order in schema.prisma / activity.types.ts.
const REGION_ORDER: readonly string[] = [
  'SOUTH_BAY',
  'PENINSULA',
  'EAST_BAY',
  'SAN_FRANCISCO',
  'OTHER',
];

/**
 * Format a single Region enum value as a human-readable label.
 * Returns "" when region is null/undefined or unknown; caller decides fallback.
 */
export function formatRegion(region: RegionLike, locale: Locale): string {
  if (region == null) return '';
  return REGION_LABELS[region]?.[locale] ?? '';
}

function canonicalRegions(regions: readonly string[]): string[] {
  const seen = new Set<string>();
  const filtered: string[] = [];
  for (const r of regions) {
    if (REGION_LABELS[r] && !seen.has(r)) {
      seen.add(r);
      filtered.push(r);
    }
  }
  return filtered.sort((a, b) => REGION_ORDER.indexOf(a) - REGION_ORDER.indexOf(b));
}

/**
 * Format an array of Region enum values as a single string.
 *
 * - 'full' (default): join all regions with locale-aware separator, e.g. "南湾、半岛、东湾"
 * - 'smart': ≤2 regions → full; ≥3 regions → first 2 + "+N", e.g. "南湾、半岛 +1"
 *
 * Input is deduped and sorted by enum-definition order so output is stable regardless of input order.
 * Unknown values are silently dropped.
 */
export function formatRegions(
  regions: RegionArrayLike,
  locale: Locale,
  mode: 'full' | 'smart' = 'full'
): string {
  if (!regions || regions.length === 0) return '';
  const canonical = canonicalRegions(regions);
  if (canonical.length === 0) return '';

  const sep = locale === 'en' ? ', ' : '、';
  const labels = canonical.map((r) => REGION_LABELS[r][locale]);

  if (mode === 'smart' && canonical.length > 2) {
    const head = labels.slice(0, 2).join(sep);
    const extra = canonical.length - 2;
    return `${head} +${extra}`;
  }
  return labels.join(sep);
}

function nonEmpty(s: string | null | undefined): string | null {
  if (s == null) return null;
  const trimmed = s.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Format an activity's location for display on detail pages, newsletter, and emails.
 * (Activity cards including the Calendar view show region only via `formatRegions`;
 * see docs/prd-multi-region.md Amendments 2026-05-14.)
 *
 * Priority (PRD §4.1) — once a higher-priority value is present, lower ones are ignored:
 *   1. locationDetails (trim non-empty)
 *   2. region[] (non-empty after dedup) — formatted via formatRegions(mode)
 *   3. fallbackLocation (legacy free-text venue, will be dropped post Phase 6)
 *   4. ""
 *
 * Note: details and region are NOT concatenated. If both exist, details wins.
 *
 * `regionMode` controls multi-region rendering: 'full' (default, used for detail/email views)
 * vs 'smart' (used for cards/calendar where space is tight; truncates to "first2 +N").
 */
export function formatActivityLocation(params: {
  region: RegionArrayLike;
  locationDetails: string | null | undefined;
  fallbackLocation?: string | null | undefined;
  locale: Locale;
  regionMode?: 'full' | 'smart';
}): string {
  const { region, locationDetails, fallbackLocation, locale, regionMode = 'full' } = params;

  const details = nonEmpty(locationDetails);
  if (details) return details;

  const regionLabel = formatRegions(region, locale, regionMode);
  if (regionLabel) return regionLabel;

  return nonEmpty(fallbackLocation) ?? '';
}
